import type { HookAPI } from 'antd/es/modal/useModal'
import { useState } from 'react'

import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { OrganisationShortType, UserShortType } from '@/types'

import {
  isScrolledToBottom,
  type OrgScope,
  type TargetType,
} from './SubscriptionCreationModal.config'
import useOrganisationSearch from './useOrganisationSearch'
import useSubscriptionUsers from './useSubscriptionUsers'

function useSubscriptionTarget(modal: HookAPI) {
  const myOrgCode = useUserInfoStore(
    (state) => state.currentUser?.organisationShortModel?.code
  )

  const [targetType, setTargetType] = useState<TargetType>('SELF')
  const [orgScope, setOrgScope] = useState<OrgScope>('MY')
  const [orgValue, setOrgValue] = useState<string | undefined>(undefined)
  const [selectedOrg, setSelectedOrg] = useState<OrganisationShortType | null>(
    null
  )
  const [selectedUser, setSelectedUser] = useState<UserShortType | null>(null)
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [chatId, setChatId] = useState('')

  const users = useSubscriptionUsers()
  const orgSearch = useOrganisationSearch(modal)

  const changeTargetType = (nextTargetType: TargetType) => {
    setTargetType(nextTargetType)
    setSelectedUser(null)
    setSelectedRole(null)
    setChatId('')
    if (nextTargetType === 'USER' && orgScope === 'MY') {
      users.loadMyOrgUsers(myOrgCode)
    }
  }

  const changeOrgScope = (nextOrgScope: OrgScope) => {
    setOrgScope(nextOrgScope)
    setSelectedOrg(null)
    setOrgValue(undefined)
    orgSearch.clearOptions()
    setSelectedUser(null)
    users.clearUsers()
    if (nextOrgScope === 'MY') users.loadMyOrgUsers(myOrgCode)
  }

  const selectOrg = (id: string) => {
    const option = orgSearch.options.find((item) => item.value === id)
    if (!option) return
    setSelectedOrg(option.org)
    setOrgValue(id)
    orgSearch.setSearchValue(option.org.description)
    setSelectedUser(null)
    users.loadOrgUsers(option.org.code)
  }

  const clearOrg = () => {
    setSelectedOrg(null)
    setOrgValue(undefined)
    orgSearch.resetQuery()
    users.clearUsers()
    setSelectedUser(null)
  }

  const handleUsersScroll = (event: React.UIEvent<HTMLUListElement>) => {
    if (!isScrolledToBottom(event.currentTarget)) return
    const isMyOrg = orgScope === 'MY'
    users.loadMoreUsers(isMyOrg ? myOrgCode : selectedOrg?.code, isMyOrg)
  }

  return {
    targetType,
    orgScope,
    orgValue,
    selectedOrg,
    selectedUser,
    selectedRole,
    chatId,
    users,
    orgSearch,
    changeTargetType,
    changeOrgScope,
    selectOrg,
    clearOrg,
    setSelectedUser,
    setSelectedRole,
    setChatId,
    handleUsersScroll,
  }
}

export type SubscriptionTargetState = ReturnType<typeof useSubscriptionTarget>

export default useSubscriptionTarget
